import { skyTerrainHeight } from '../../../packages/domain/src/87-sky-terrain.mjs';
import { SKY_WORLD } from '../../../packages/domain/src/86-sky-world-scale.mjs';
import { skyGuardianAnatomy } from '../../../packages/domain/src/83-sky-anatomy.mjs';
import { skyBossDesign } from '../../../packages/domain/src/85-sky-boss-designs.mjs';
import { ornamentSkyBoss } from './127-sky-boss-silhouettes.mjs';
/** Own guardian surface detail, armor deterioration and targeting presentation.
 * The caller owns the resource Set and domain time; this factory creates no
 * clocks or gameplay policy. releaseEnemy disposes only encounter-local materials. */
export function createGuardianArt(T, models, resources) {
  const debrisBounds = new T.Box3(),
    debrisPosition = new T.Vector3();
  function roundedDragonCrown(group, head) {
    const broad = group.userData.profile === 'twin';
    const cache = broad ? 'wyvernCrown' : 'serpentCrown';
    if (!models.sentinel[cache]) {
      const curve = new T.CatmullRomCurve3([
        new T.Vector3(0, 0, 0),
        new T.Vector3(0.25, 0.5, -0.15),
        new T.Vector3(broad ? 1.25 : 0.65, 1.1, -0.55),
        new T.Vector3(broad ? 1.65 : 0.72, 1.85, -1.15),
        new T.Vector3(broad ? 1.5 : 0.38, 2.2, -1.5),
      ]);
      const horn = new T.TubeGeometry(curve, 40, 0.24, 12, false);
      const position = horn.attributes.position;
      for (let ring = 0; ring <= 40; ring++) {
        const t = ring / 40,
          center = curve.getPointAt(t);
        const radius = Math.pow(1 - t, 0.72) * 0.97 + 0.03;
        for (let vertex = 0; vertex <= 12; vertex++) {
          const index = ring * 13 + vertex;
          const point = new T.Vector3()
            .fromBufferAttribute(position, index)
            .sub(center)
            .multiplyScalar(radius)
            .add(center);
          position.setXYZ(index, point.x, point.y, point.z);
        }
      }
      horn.computeVertexNormals();
      const outline = new T.Shape();
      outline.moveTo(0, 0);
      outline.bezierCurveTo(0.4, 0.4, 1.1, 0.8, broad ? 2.15 : 1.5, 1.15);
      outline.bezierCurveTo(1.3, 0.65, 0.95, 0.2, 1.5, -0.05);
      outline.bezierCurveTo(0.95, 0.15, 0.65, -0.12, 0.95, -0.55);
      outline.bezierCurveTo(0.5, -0.3, 0.1, -0.4, 0, 0);
      const frill = new T.ExtrudeGeometry(outline, {
        depth: 0.08,
        bevelEnabled: true,
        bevelSize: 0.045,
        bevelThickness: 0.04,
        bevelSegments: 4,
        curveSegments: 20,
        steps: 1,
      });
      const hornMaterial = new T.MeshStandardMaterial({
        color: '#927050',
        roughness: 0.5,
        metalness: 0.58,
      });
      const frillMaterial = new T.MeshStandardMaterial({
        color: '#38656b',
        roughness: 0.66,
        metalness: 0.28,
        side: T.DoubleSide,
      });
      models.sentinel[cache] = { horn, frill, hornMaterial, frillMaterial };
      for (const resource of [horn, frill, hornMaterial, frillMaterial]) resources.add(resource);
    }
    const { horn, frill, hornMaterial, frillMaterial } = models.sentinel[cache];
    for (const side of [-1, 1]) {
      const antler = new T.Mesh(horn, hornMaterial);
      antler.name = 'layered cranial crest';
      antler.position.set(side * 0.64, 0.6, -0.1);
      antler.scale.x = side;
      head.add(antler);
      const membrane = new T.Mesh(frill, frillMaterial);
      membrane.name = 'curved dragon cheek frill';
      membrane.position.set(side * 0.75, 0.12, -0.38);
      membrane.scale.x = side;
      membrane.rotation.y = side * -0.3;
      head.add(membrane);
    }
  }
  function ornamentGuardian(group) {
    if (group.userData.artVariant === 'tidal-oracle') return;
    ornamentSkyBoss(T, group, models, resources);
    let sculpted = false;
    group.traverse((node) => {
      if (/sculpted|rounded_carved|rounded_leviathan|^weakpoint_(core|left|right)/.test(node.name))
        sculpted = true;
    });
    if (sculpted) return;

    // Authored swept crown blades add a layered silhouette to the existing
    // cranial armor; these are accents, never replacements for the GLB forms.
    const heads = [];
    group.traverse((node) => {
      if (!node.isMesh && /^head(?:[._]?\d+|_right)?$/.test(node.name)) heads.push(node);
    });
    const head = heads[0];
    if (!head) return;
    if (['serpent', 'twin', 'skeletalTwin', 'skeletalSerpent'].includes(group.userData.profile)) {
      for (const dragonHead of heads) roundedDragonCrown(group, dragonHead);
      return;
    }
    if (!models.sentinel.crownGeometry) {
      const shape = new T.Shape();
      shape.moveTo(0, 0);
      shape.bezierCurveTo(0.22, 0.32, 0.72, 0.55, 0.92, 1.65);
      shape.bezierCurveTo(0.46, 1.36, 0.18, 0.91, -0.12, 0.3);
      shape.closePath();
      models.sentinel.crownGeometry = new T.ExtrudeGeometry(shape, {
        depth: 0.055,
        bevelEnabled: true,
        bevelThickness: 0.025,
        bevelSize: 0.025,
        bevelSegments: 4,
        steps: 1,
        curveSegments: 20,
      });
      models.sentinel.crownMaterial = new T.MeshStandardMaterial({
        color: '#97734c',
        roughness: 0.43,
        metalness: 0.78,
      });
      resources.add(models.sentinel.crownGeometry);
      resources.add(models.sentinel.crownMaterial);
    }
    for (const side of [-1, 1])
      for (let layer = 0; layer < 3; layer++) {
        const blade = new T.Mesh(models.sentinel.crownGeometry, models.sentinel.crownMaterial);
        blade.name = 'layered cranial crest';
        blade.position.set(side * (0.36 + layer * 0.13), 0.48 - layer * 0.13, 0.02 - layer * 0.16);
        blade.scale.set(side * (0.68 + layer * 0.1), 0.64 + layer * 0.13, 1);
        blade.rotation.z = -side * layer * 0.13;
        head.add(blade);
      }
  }
  function attachWeakpoint(group) {
    const descriptor = skyGuardianAnatomy({
      profile: group.userData.profile,
      artVariant: group.userData.artVariant,
      prototypeRig: group.userData.prototypeRig,
    }).targets;
    const bindings = [];
    group.traverse((node) => {
      if (node.isMesh || !/^weakpoint_(core|left|right)(?:[._]?\d+)?$/.test(node.name)) return;
      let top = node;
      while (top.parent && top.parent !== group) top = top.parent;
      const clone = top.userData.guardianCloneIndex ?? (group.children.indexOf(top) === 0 ? 0 : -1);
      const parentNames = [];
      let ancestor = node.parent;
      while (ancestor && ancestor !== group) {
        parentNames.push(ancestor.name.replace(/[._]?\d+$/, ''));
        ancestor = ancestor.parent;
      }
      const target = descriptor.find(
        (value) =>
          value.clone === clone &&
          node.name.replace(/[._]?\d+$/, '') === value.node &&
          (!value.parent || parentNames.includes(value.parent)),
      );
      if (!target) return;
      const surfaces = [],
        covers = [];
      node.traverse((child) => {
        if (child.isMesh && /weakpoint_surface/.test(child.name)) surfaces.push(child);
        if (/weakpoint_cover_(left|right)/.test(child.name))
          covers.push({
            node: child,
            position: child.position.clone(),
            rotation: child.rotation.clone(),
          });
      });
      bindings.push({ node, id: target.id, surfaces, covers });
    });
    group.userData.anatomicalTargets = bindings;
  }
  function prepareGuardian(group) {
    const uniforms = { damage: { value: 0 }, collapse: { value: 0 } };
    const materials = new Map();
    group.userData.damageUniforms = uniforms;
    group.userData.armorPanels = [];
    group.traverse((node) => {
      if (!node.isMesh) return;
      const transform = (source) => {
        // Paired apertures alternate independently even when their GLB shares one material.
        const key = /weakpoint_surface/.test(node.name) ? `${node.uuid}:${source.uuid}` : source;
        if (materials.has(key)) return materials.get(key);
        const material = source.clone();
        const design = skyBossDesign(group.userData.design);
        if (
          design &&
          group.userData.design !== 0 &&
          group.userData.artVariant !== 'tidal-oracle' &&
          !/weakpoint|articulated_/.test(node.name)
        ) {
          material.color?.lerp(new T.Color(design.shell), 0.42);
        }
        material.onBeforeCompile = (shader) => {
          shader.uniforms.skyDamage = uniforms.damage;
          shader.uniforms.skyCollapse = uniforms.collapse;
          shader.vertexShader =
            'varying vec3 skySurface;\nuniform float skyCollapse;\n' + shader.vertexShader;
          shader.vertexShader = shader.vertexShader.replace(
            '#include <begin_vertex>',
            `#include <begin_vertex>\nskySurface = position;\ntransformed *= 1.0 - ${group.userData.profile === 'stoneOracle' ? '0.0' : '0.25'} * smoothstep(0.75, 1.0, skyCollapse);`,
          );
          shader.fragmentShader =
            'varying vec3 skySurface;\nuniform float skyDamage;\nuniform float skyCollapse;\n' +
            shader.fragmentShader;
          shader.fragmentShader = shader.fragmentShader.replace(
            '#include <color_fragment>',
            `#include <color_fragment>
            vec3 p = skySurface;
            float grain = sin(p.x*87.0 + sin(p.z*33.0))*sin(p.y*91.0);
            float etch = pow(abs(sin(p.y*23.0 + p.z*6.0)), 34.0);
            float field = sin(p.x*5.2 + sin(p.y*7.0))*sin(p.z*6.1 + p.y*2.0);
            float crackWidth = mix(0.022,0.105,skyDamage);
            float crack = 1.0-smoothstep(crackWidth,crackWidth+0.045,abs(field));
            float wound = 1.0-smoothstep(0.012,crackWidth*0.45,abs(field));
            float exposed = smoothstep(0.18,0.8,skyDamage);
            diffuseColor.rgb *= (0.94+grain*0.045-etch*0.07)*(1.0-exposed*0.72);
            diffuseColor.rgb *= 1.0-crack*exposed*0.65;
            diffuseColor.a *= (1.0-wound*smoothstep(0.48,0.94,skyDamage)*0.85)*(1.0-smoothstep(0.7,1.0,skyCollapse));`,
          );
          shader.fragmentShader = shader.fragmentShader.replace(
            '#include <emissivemap_fragment>',
            `#include <emissivemap_fragment>
            totalEmissiveRadiance += vec3(1.0,0.19,0.035)*(crack-wound*0.7)*exposed*(1.8+skyCollapse*3.0);`,
          );
        };
        material.customProgramCacheKey = () =>
          `sky-guardian-weathering-v3-${group.userData.profile === 'stoneOracle'}`;
        material.transparent = true;
        resources.add(material);
        materials.set(key, material);
        return material;
      };
      node.material = Array.isArray(node.material)
        ? node.material.map(transform)
        : transform(node.material);
      if (
        /flank[ _]plate|feather[ _]blade|gauntlet[ _]shield|pectoral[ _]plate|armor[ _]skirt|cheek[ _]blade|shell|turbine[ _]fairing|shoulder|knuckle|dorsal[ _]massif/i.test(
          node.name,
        )
      ) {
        const index = group.userData.armorPanels.length;
        if (index < 18)
          group.userData.armorPanels.push({
            node,
            position: node.position.clone(),
            rotation: node.rotation.clone(),
            threshold: 0.3 + (index % 5) * 0.12,
            direction: new T.Vector3(index % 2 ? 1 : -1, 0.35 + (index % 3) * 0.2, 0.4).normalize(),
          });
      }
    });
    group.userData.guardianMaterials = [...materials.values()];
  }
  function releaseEnemy(group) {
    for (const material of group.userData.guardianMaterials ?? []) {
      resources.delete(material);
      material.dispose();
    }
    group.userData.guardianMaterials = [];
  }
  function updateDamage(group, item, reduced) {
    const damage =
      Number.isFinite(item.maxHp) && item.maxHp > 0
        ? T.MathUtils.clamp(1 - (item.hp ?? item.maxHp) / item.maxHp, 0, 1)
        : 0;
    const collapse = T.MathUtils.clamp((item.deathAge ?? 0) / (item.deathDuration ?? 2.4), 0, 1);
    if (group.userData.damageUniforms) {
      group.userData.damageUniforms.damage.value = Math.max(damage, collapse > 0 ? 1 : 0);
      group.userData.damageUniforms.collapse.value = collapse;
    }
    for (const panel of group.userData.armorPanels ?? []) {
      const loss = T.MathUtils.smoothstep(damage, panel.threshold, panel.threshold + 0.16);
      const stone = group.userData.profile === 'stoneOracle';
      const breakaway = T.MathUtils.smoothstep(collapse, stone ? 0.38 : 0.22, 0.9);
      const eject = reduced ? 0 : breakaway * breakaway * (stone ? 5 : 12);
      panel.node.position.copy(panel.position).addScaledVector(panel.direction, loss * 0.2 + eject);
      if (stone && !reduced) panel.node.position.y -= breakaway * breakaway * 7;
      panel.node.rotation.copy(panel.rotation);
      if (!reduced)
        panel.node.rotation.z += (loss * 0.08 + collapse * 2) * (panel.direction.x > 0 ? 1 : -1);
      panel.node.visible = collapse < 0.88 && (loss < 0.99 || collapse > 0);
      if (stone && collapse > 0 && panel.node.visible && !reduced) {
        // Released stone slabs settle on the physical surface before fading.
        // This only arrests cosmetic debris; live hit-bearing anatomy is pure83.
        panel.node.updateWorldMatrix(true, false);
        if (!panel.node.geometry.boundingBox) panel.node.geometry.computeBoundingBox();
        debrisBounds.copy(panel.node.geometry.boundingBox).applyMatrix4(panel.node.matrixWorld);
        const terrainY = Number.isFinite(item.terrainTravel)
          ? Math.max(
              ...[debrisBounds.min.x, debrisBounds.max.x].flatMap((x) =>
                [debrisBounds.min.z, debrisBounds.max.z].map((z) =>
                  skyTerrainHeight(x, z, item.terrainTravel),
                ),
              ),
            ) + 0.015
          : SKY_WORLD.groundY;
        if (debrisBounds.min.y < terrainY) {
          panel.node.getWorldPosition(debrisPosition);
          debrisPosition.y += terrainY - debrisBounds.min.y;
          panel.node.position.copy(panel.node.parent.worldToLocal(debrisPosition));
        }
      }
    }
  }
  function updateIris(group, item, reduced = false) {
    const bindings = group.userData.anatomicalTargets ?? [];
    if (!bindings.length) return;
    const anatomy =
      item.anatomy ??
      skyGuardianAnatomy({ ...item, profile: item.profile ?? group.userData.profile });
    for (const binding of bindings) {
      const target = anatomy.targets.find((point) => point.id === binding.id);
      if (!target) continue;
      const warning = target.telegraph
        ? reduced
          ? 0.4
          : 0.3 + Math.sin((item.age ?? 0) * 9) * 0.15
        : 0;
      const flash =
        !reduced &&
        item.impactQuality === 'critical' &&
        (item.age ?? 0) - (item.impactAt ?? -10) < 0.12;
      for (const surface of binding.surfaces) {
        for (const material of Array.isArray(surface.material)
          ? surface.material
          : [surface.material]) {
          material.emissive?.set(flash ? '#fff1c4' : target.exposed ? '#e8a839' : '#895630');
          material.emissiveIntensity = item.defeated
            ? 0
            : target.exposed
              ? flash
                ? 2.3
                : 1.25
              : warning + 0.035;
        }
      }
      for (const cover of binding.covers) {
        const side = cover.node.name.includes('left') ? -1 : 1;
        cover.node.position.copy(cover.position);
        cover.node.rotation.copy(cover.rotation);
        const oracle = group.userData.artVariant === 'tidal-oracle';
        cover.node.position.x += side * target.opening * (oracle ? 0.57 : 0.62);
        cover.node.rotation.y += side * target.opening * (oracle ? 0.2 : 0.5);
      }
    }
  }
  return {
    ornamentGuardian,
    attachWeakpoint,
    prepareGuardian,
    releaseEnemy,
    updateDamage,
    updateIris,
  };
}
